import { createConsumer } from '@rails/actioncable'

const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor
const BROWSER_ID_KEY = 'remote-debugging-browser-id'
const CONSOLE_METHODS = ['debug', 'error', 'info', 'log', 'warn']
const HEARTBEAT_INTERVAL = 30_000
const MAX_COLLECTION_ITEMS = 100
const MAX_DEPTH = 6
const MAX_RESULT_BYTES = 64 * 1024

let diagnosticsConsumer
let heartbeatTimer
let subscription
let subscriptionUserId
let tabId
const executedIds = new Set()

function captureConsole () {
  const entries = []
  const originals = {}

  CONSOLE_METHODS.forEach((method) => {
    originals[method] = console[method]
    console[method] = (...args) => {
      entries.push({ level: method, values: normalize(args) })
      originals[method].apply(console, args)
    }
  })

  return {
    entries,
    restore: () => {
      CONSOLE_METHODS.forEach((method) => {
        console[method] = originals[method]
      })
    }
  }
}

function enabled () {
  return document.querySelector('meta[name="remote-debugging-enabled"]')?.content === 'true'
}

function enabledUserId () {
  return document.querySelector('meta[name="remote-debugging-user-id"]')?.content
}

async function execute (message) {
  if (executedIds.has(message.execution_id) || new Date(message.expires_at) <= new Date()) return

  executedIds.add(message.execution_id)
  subscription.perform('started', identity(message))

  const startedAt = performance.now()
  const capturedConsole = captureConsole()
  let timeoutId

  try {
    const run = new AsyncFunction(message.source)
    const timeout = new Promise((_resolve, reject) => {
      timeoutId = window.setTimeout(
        () => reject(new Error('Execution timed out')),
        message.timeout_ms
      )
    })
    const value = await Promise.race([run(), timeout])
    transmitResult(message, {
      console_output: capturedConsole.entries,
      duration_ms: Math.round(performance.now() - startedAt),
      result: normalize(value),
      status: 'succeeded'
    })
  } catch (error) {
    transmitResult(message, {
      console_output: capturedConsole.entries,
      duration_ms: Math.round(performance.now() - startedAt),
      error: normalizeError(error),
      status: 'failed'
    })
  } finally {
    window.clearTimeout(timeoutId)
    capturedConsole.restore()
  }
}

function identity (message) {
  return { execution_id: message.execution_id, nonce: message.nonce }
}

function metadata () {
  return {
    language: navigator.language,
    mobile: /Mobi/i.test(navigator.userAgent),
    platform: navigator.platform,
    pwa: window.matchMedia('(display-mode: standalone)').matches,
    screen_height: window.screen.height,
    screen_width: window.screen.width
  }
}

function normalize (value, depth = 0, seen = new WeakSet()) {
  if (value === null || ['boolean', 'number', 'string'].includes(typeof value)) return value
  if (typeof value === 'bigint') return `${value}n`
  if (typeof value === 'undefined') return '[undefined]'
  if (typeof value === 'function') return `[Function ${value.name || 'anonymous'}]`
  if (typeof value === 'symbol') return value.toString()
  if (depth >= MAX_DEPTH) return '[Maximum depth reached]'
  if (value instanceof Error) return normalizeError(value)
  if (value instanceof window.Element) {
    return `[Element ${value.tagName.toLowerCase()}${value.id ? `#${value.id}` : ''}]`
  }
  if (seen.has(value)) return '[Circular]'

  seen.add(value)
  if (Array.isArray(value)) {
    return value.slice(0, MAX_COLLECTION_ITEMS).map(item => normalize(item, depth + 1, seen))
  }

  const output = {}
  Object.entries(value).slice(0, MAX_COLLECTION_ITEMS).forEach(([key, item]) => {
    try {
      output[key] = normalize(item, depth + 1, seen)
    } catch (error) {
      output[key] = `[Unreadable: ${error.message}]`
    }
  })
  return output
}

function normalizeError (error) {
  return {
    message: error?.message || String(error),
    name: error?.name || 'Error',
    stack: error?.stack?.slice(0, 10_000)
  }
}

function randomId () {
  if (window.crypto.randomUUID) return window.crypto.randomUUID()

  const bytes = window.crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20)}`
}

function stableId (storage, key) {
  let value = storage.getItem(key)
  if (!value) {
    value = randomId()
    storage.setItem(key, value)
  }
  return value
}

function startSubscription (userId) {
  if (subscription || !userId) return

  tabId ||= randomId()
  subscriptionUserId = userId
  diagnosticsConsumer = createConsumer('/cable?remote_debugging=true')
  subscription = diagnosticsConsumer.subscriptions.create({
    channel: 'RemoteDebuggingChannel',
    browser_id: stableId(window.localStorage, BROWSER_ID_KEY),
    current_url: window.location.href,
    metadata: metadata(),
    tab_id: tabId,
    user_agent: navigator.userAgent
  }, {
    connected () {
      sendHeartbeat()
    },

    received (message) {
      if (message.type === 'disabled') {
        stopSubscription()
      } else if (message.type === 'execution_available') {
        subscription.perform('claim', identity(message))
      } else if (message.type === 'execute') {
        execute(message)
      }
    }
  })

  heartbeatTimer = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL)
}

function sendHeartbeat () {
  subscription?.perform('heartbeat', { current_url: window.location.href })
}

function stopSubscription () {
  window.clearInterval(heartbeatTimer)
  heartbeatTimer = null
  subscription?.unsubscribe()
  subscription = null
  subscriptionUserId = null
  diagnosticsConsumer?.disconnect()
  diagnosticsConsumer = null
}

function syncSubscription () {
  const userId = enabled() ? enabledUserId() : null
  if (subscription && subscriptionUserId !== userId) stopSubscription()

  if (userId) {
    startSubscription(userId)
    sendHeartbeat()
  } else {
    stopSubscription()
  }
}

function transmitResult (message, result) {
  const payload = { ...identity(message), ...result }
  const serialized = JSON.stringify(payload)

  if (new Blob([serialized]).size > Math.min(message.max_result_bytes, MAX_RESULT_BYTES)) {
    subscription.perform('complete', {
      ...identity(message),
      error: { message: 'Result exceeded the browser limit', name: 'ResultTooLarge' },
      output_truncated: true,
      status: 'failed'
    })
    return
  }

  subscription.perform('complete', payload)
}

document.addEventListener('turbo:load', syncSubscription)
syncSubscription()
